import { Moment } from 'moment-timezone';
import { AddonSchedule, AddonScheduleMode } from 'types/AddonSchedule';
import { AddonService } from 'types/AddonService';
import {
    isOnlineBookingBoardingItem,
    isOnlineBookingDaycareItem,
    OnlineBookingBoardingItem,
    OnlineBookingDaycareItem,
    OnlineBookingItem
} from 'types/OnlineBooking';

import { createDefaultSchedule, getScheduleOccurrences } from './addonSchedule';

const DATE_FORMAT = 'YYYY-MM-DD';

// Billed nights of a stay, as days in the stay's (marketplace) time zone: check-out day left out,
// a same-day stay keeps its one day. Same rule the backend uses for Add-Ons on an existing booking.
const getBoardingAddonDays = (item: OnlineBookingBoardingItem): Array<Moment> => {
    if (!item.startDate || !item.endDate) {
        return [];
    }

    const days: Array<Moment> = [];
    const day = item.startDate.clone().startOf('day');
    const last = item.endDate.clone().startOf('day');

    do {
        days.push(day.clone());
        day.add(1, 'day');
    } while (day.isBefore(last));

    return days;
};

export const getBoardingAddonDates = (item: OnlineBookingBoardingItem): Array<string> =>
    getBoardingAddonDays(item).map(day => day.format(DATE_FORMAT));

// Booked days of a daycare item, in the day picker's (marketplace) time zone, earliest first.
const getDaycareAddonDays = (item: OnlineBookingDaycareItem): Array<Moment> =>
    (item.days ?? [])
        .map(day => day.date.clone().startOf('day'))
        .sort((a, b) => a.valueOf() - b.valueOf());

export const getDaycareAddonDates = (item: OnlineBookingDaycareItem): Array<string> =>
    getDaycareAddonDays(item).map(day => day.format(DATE_FORMAT));

const getAddonDays = (item: OnlineBookingItem): Array<Moment> => {
    if (isOnlineBookingBoardingItem(item)) {
        return getBoardingAddonDays(item);
    }

    return isOnlineBookingDaycareItem(item) ? getDaycareAddonDays(item) : [];
};

/** The days an item's Add-Ons can be scheduled on (nights of a stay, booked daycare days). */
export const getAddonScheduleDates = (item: OnlineBookingItem): Array<string> =>
    getAddonDays(item).map(day => day.format(DATE_FORMAT));

// The stay the Add-Ons were set for, "YYYY-MM-DD/YYYY-MM-DD", or undefined while a date is missing.
export const getBoardingStayKey = (item: OnlineBookingBoardingItem): string | undefined =>
    item.startDate && item.endDate
        ? `${item.startDate.format(DATE_FORMAT)}/${item.endDate.format(DATE_FORMAT)}`
        : undefined;

// The same for the days of a daycare item, "YYYY-MM-DD,YYYY-MM-DD", or undefined with no day.
export const getDaycareStayKey = (item: OnlineBookingDaycareItem): string | undefined =>
    item.days?.length ? getDaycareAddonDates(item).join(',') : undefined;

export const getAddonScheduleStayKey = (item: OnlineBookingItem): string | undefined => {
    if (isOnlineBookingBoardingItem(item)) {
        return getBoardingStayKey(item);
    }

    return isOnlineBookingDaycareItem(item) ? getDaycareStayKey(item) : undefined;
};

/**
 * A new stay restores the Add-Ons: they stay selected, each back on the default (Everyday x1) over
 * the new days. The same stay, or one still being picked, keeps them as they are.
 */
const restoreAddonsOnStayChange = <T extends OnlineBookingBoardingItem | OnlineBookingDaycareItem>(
    item: T,
    stay: string | undefined
): T => {
    if (!item.addonSchedules || !stay || stay === item.addonSchedulesStay) {
        return item;
    }

    return {
        ...item,
        addonSchedules: Object.fromEntries(
            Object.keys(item.addonSchedules).map(addonId => [addonId, createDefaultSchedule()])
        ),
        addonSchedulesStay: stay
    };
};

export const resetBoardingAddonsOnStayChange = (
    item: OnlineBookingBoardingItem
): OnlineBookingBoardingItem => restoreAddonsOnStayChange(item, getBoardingStayKey(item));

export const resetDaycareAddonsOnDaysChange = (
    item: OnlineBookingDaycareItem
): OnlineBookingDaycareItem => restoreAddonsOnStayChange(item, getDaycareStayKey(item));

/** Selected Add-Ons left on Select Days with no day: they block the step until fixed or unselected. */
export const getAddonsMissingDays = (item: OnlineBookingItem): Array<AddonService> =>
    item.addons.filter(
        addon =>
            item.addonSchedules?.[addon.id]?.mode === AddonScheduleMode.SelectDays &&
            getOnlineBookingAddonUnits(item, addon) === 0
    );

export interface DatedAddonOccurrence {
    date: Moment;
    quantity: number;
}

const getScheduledOccurrences = (
    days: Array<Moment>,
    schedule: AddonSchedule
): Array<DatedAddonOccurrence> => {
    const dayByDate = new Map(days.map(day => [day.format(DATE_FORMAT), day]));

    return getScheduleOccurrences(schedule, Array.from(dayByDate.keys())).map(occurrence => ({
        // The item's own day, so the Add-On starts in the marketplace time zone, not the browser's.
        date: dayByDate.get(occurrence.date)!.clone(),
        quantity: occurrence.quantity
    }));
};

/** One dated occurrence per selected day, or undefined when the add-on has no schedule yet. */
export const getBoardingAddonOccurrences = (
    item: OnlineBookingBoardingItem,
    addon: AddonService
): Array<DatedAddonOccurrence> | undefined => {
    const schedule = item.addonSchedules?.[addon.id];

    return schedule ? getScheduledOccurrences(getBoardingAddonDays(item), schedule) : undefined;
};

/** The same for daycare, where no schedule means once on every booked day, as it always was. */
export const getDaycareAddonOccurrences = (
    item: OnlineBookingDaycareItem,
    addon: AddonService
): Array<DatedAddonOccurrence> =>
    getScheduledOccurrences(
        getDaycareAddonDays(item),
        item.addonSchedules?.[addon.id] ?? createDefaultSchedule()
    );

export const getBoardingAddonUnits = (
    item: OnlineBookingBoardingItem,
    addon: AddonService
): number | undefined =>
    getBoardingAddonOccurrences(item, addon)?.reduce((units, o) => units + o.quantity, 0);

/**
 * Quantity of each order line an Add-On creates for a booking item: one line per scheduled day in
 * boarding and daycare, each with its quantity; anything else (grooming, evaluation) has a single
 * line with the quantity picked for it, one by default.
 */
export const getOnlineBookingAddonLineQuantities = (
    item: OnlineBookingItem,
    addon: AddonService
): Array<number> => {
    const getQuantities = (occurrences: Array<DatedAddonOccurrence>) =>
        occurrences.map(occurrence => occurrence.quantity);

    if (isOnlineBookingBoardingItem(item)) {
        const occurrences = getBoardingAddonOccurrences(item, addon);

        return occurrences ? getQuantities(occurrences) : [1];
    }

    if (isOnlineBookingDaycareItem(item)) {
        return getQuantities(getDaycareAddonOccurrences(item, addon));
    }

    return [item.addonSchedules?.[addon.id]?.quantity ?? 1];
};

// pet x Add-On x date x quantity of one booking item.
export const getOnlineBookingAddonUnits = (item: OnlineBookingItem, addon: AddonService): number =>
    getOnlineBookingAddonLineQuantities(item, addon).reduce(
        (units, quantity) => units + quantity,
        0
    );
